import { useState, useEffect } from "react";
import { X, Cookie } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { analytics, updateConsent } from "@/lib/analytics";

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already responded to cookie consent
    try {
      const consent = localStorage.getItem("cookieConsent");
      if (!consent) {
        // Show banner after a short delay for better UX
        setTimeout(() => setShowBanner(true), 1000);
      } else if (consent === "accepted") {
        // Re-apply granted consent on subsequent visits so tags fire as expected.
        updateConsent(true);
      } else {
        updateConsent(false);
      }
    } catch (error) {
      // If localStorage is blocked (incognito, privacy settings), don't show banner
      console.log("Cookie consent: localStorage not available");
      setShowBanner(false);
    }
  }, []);

  const handleAccept = () => {
    try {
      localStorage.setItem("cookieConsent", "accepted");
      updateConsent(true);
      analytics.acceptCookies();
    } catch (error) {
      console.log("Cookie consent: Could not save preference");
    }
    setShowBanner(false);
  };

  const handleDecline = () => {
    try {
      localStorage.setItem("cookieConsent", "declined");
      updateConsent(false);
      analytics.declineCookies();
    } catch (error) {
      console.log("Cookie consent: Could not save preference");
    }
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6 animate-slide-in-bottom">
      <div className="max-w-6xl mx-auto bg-deep-navy/95 backdrop-blur-md border-2 border-cyan/40 rounded-lg shadow-2xl shadow-cyan/20 p-6 md:p-8">
        <button
          onClick={handleDecline}
          className="absolute top-4 right-4 text-medium-gray hover:text-cyan transition-colors"
          aria-label="Close cookie banner"
        >
          <X size={20} />
        </button>

        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
          {/* Icon */}
          <div className="flex-shrink-0">
            <div className="w-12 h-12 rounded-full bg-cyan/10 flex items-center justify-center">
              <Cookie className="w-6 h-6 text-cyan" />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1">
            <h3 className="text-lg font-bold text-foreground mb-2">
              We Value Your Privacy
            </h3>
            <p className="text-sm text-medium-gray leading-relaxed">
              We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. 
              By clicking "Accept All," you consent to our use of cookies. 
              Learn more in our{" "}
              <Link 
                to="/privacy" 
                className="text-cyan hover:underline font-medium"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <Button
              onClick={handleDecline}
              variant="outline"
              className="border-cyan/40 text-foreground hover:bg-cyan/10 hover:text-cyan w-full sm:w-auto"
            >
              Decline
            </Button>
            <Button
              onClick={handleAccept}
              className="bg-cyan text-white hover:bg-cyan/90 hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-cyan/50 w-full sm:w-auto"
            >
              Accept All
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
